/**
 * Breaks: the lunch hour every day, and a block on one specific date.
 *
 * *** TWO KINDS, TWO ENDPOINTS, ONE IDEA ***
 * A recurring break repeats weekly (or daily, when `day_of_week` is null).
 * A one-off blocks part of one date. Both carve time out of working hours;
 * neither cancels a booking already inside it.
 */

import { useEffect, useState } from 'react'
import { Card } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import {
  useAddOneOffBreak,
  useRemoveOneOffBreak,
  useSetMyBreaks,
} from '@/lib/queries'
import { formatDate } from '@/lib/format'
import type { BreakEntry, OneOffBreak } from '@/types/api'
import {
  AffectedBookingsDialog,
  type ProposedRange,
} from './AffectedBookingsDialog'
import styles from './AvailabilityPage.module.css'

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

function hhmm(v: string): string {
  return v.slice(0, 5)
}

function todayIso(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function BreaksSection({
  enabled,
  recurring,
  oneOff,
  timezone,
  loading,
}: {
  enabled: boolean
  recurring: BreakEntry[] | null
  oneOff: OneOffBreak[] | null
  timezone: string | null
  loading: boolean
}) {
  const toast = useToast()
  const saveBreaks = useSetMyBreaks()
  const addOneOff = useAddOneOffBreak()
  const removeOneOff = useRemoveOneOffBreak()

  const [rows, setRows] = useState<BreakEntry[]>([])
  useEffect(() => {
    if (recurring) setRows(recurring)
  }, [recurring])

  /* The one-off being composed, and its confirmation. */
  const [date, setDate] = useState(todayIso())
  const [from, setFrom] = useState('13:00')
  const [to, setTo] = useState('14:00')
  const [pending, setPending] = useState<ProposedRange | null>(null)

  if (loading) return <p className={styles.muted}>Loading your breaks…</p>

  /*
    *** THE WHOLE FEATURE CAN BE OFF ***
    The super admin switches it per client, and the server says so rather
    than returning rows nobody may save. Hidden, not disabled.
  */
  if (!enabled) {
    return (
      <Card>
        <p className={styles.muted}>
          Your institute does not use breaks. Your weekly hours decide when
          students can book you.
        </p>
      </Card>
    )
  }

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { day_of_week: null, start_time: '13:00:00', end_time: '14:00:00', label: null },
    ])
  }

  const update = (i: number, patch: Partial<BreakEntry>) => {
    setRows((prev) => prev.map((r, n) => (n === i ? { ...r, ...patch } : r)))
  }

  const saveRecurring = async () => {
    const bad = rows.find((r) => r.start_time >= r.end_time)
    if (bad) {
      toast.error('A break must finish after it starts.')
      return
    }
    try {
      await saveBreaks.mutateAsync(rows)
      toast.success('Breaks saved.')
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not save your breaks.',
      )
    }
  }

  const submitOneOff = async () => {
    try {
      await addOneOff.mutateAsync({
        break_date: date,
        start_time: `${from}:00`,
        end_time: `${to}:00`,
      })
      toast.success('Break added.')
      setPending(null)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not add that.',
      )
    }
  }

  const remove = async (id: string) => {
    try {
      await removeOneOff.mutateAsync(id)
      toast.success('Break removed.')
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not remove that.',
      )
    }
  }

  return (
    <>
      <Card>
        <h2 className={styles.sectionTitle}>Every week</h2>
        <p className={styles.sectionNote}>
          A lunch hour, or any time you are never available.
          {timezone ? ` Times are in ${timezone}.` : ''}
        </p>

        {rows.length === 0 && (
          <p className={styles.muted}>No recurring breaks.</p>
        )}

        <div className={styles.breakList}>
          {rows.map((r, i) => (
            <div key={i} className={styles.breakRow}>
              <select
                className={styles.select}
                value={r.day_of_week ?? ''}
                onChange={(e) =>
                  update(i, {
                    day_of_week:
                      e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                aria-label="Which day"
              >
                {/* Null means every day — the server's own convention. */}
                <option value="">Every day</option>
                {DAYS.map((d, n) => (
                  <option key={d} value={n}>
                    {d}
                  </option>
                ))}
              </select>

              <input
                type="time"
                className={styles.time}
                value={hhmm(r.start_time)}
                onChange={(e) =>
                  update(i, { start_time: `${e.target.value}:00` })
                }
                aria-label="Break start"
              />
              <span className={styles.toLabel}>to</span>
              <input
                type="time"
                className={styles.time}
                value={hhmm(r.end_time)}
                onChange={(e) => update(i, { end_time: `${e.target.value}:00` })}
                aria-label="Break finish"
              />

              <input
                className={styles.labelInput}
                value={r.label ?? ''}
                onChange={(e) => update(i, { label: e.target.value || null })}
                placeholder="Lunch"
                maxLength={100}
                aria-label="What this break is"
              />

              <button
                type="button"
                className={styles.removeX}
                onClick={() => setRows((p) => p.filter((_, n) => n !== i))}
                aria-label="Remove this break"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          <Button variant="ghost" onClick={addRow}>
            Add a break
          </Button>
          <Button
            variant="primary"
            onClick={saveRecurring}
            loading={saveBreaks.isPending}
          >
            Save breaks
          </Button>
        </div>
      </Card>

      <Card>
        <h2 className={styles.sectionTitle}>One day only</h2>
        <p className={styles.sectionNote}>
          Block part of a single date — an appointment, or a meeting.
        </p>

        <div className={styles.breakRow}>
          <input
            type="date"
            className={styles.select}
            value={date}
            min={todayIso()}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Which date"
          />
          <input
            type="time"
            className={styles.time}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            aria-label="From"
          />
          <span className={styles.toLabel}>to</span>
          <input
            type="time"
            className={styles.time}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            aria-label="To"
          />
          <Button
            variant="secondary"
            onClick={() => {
              if (from >= to) {
                toast.error('The break must finish after it starts.')
                return
              }
              /*
                *** CHECK BEFORE BLOCKING ***
                The dialog previews what this would clash with. It does
                not save anything by itself.
              */
              setPending({
                start_date: date,
                end_date: date,
                start_time: `${from}:00`,
                end_time: `${to}:00`,
              })
            }}
          >
            Add
          </Button>
        </div>

        {oneOff && oneOff.length > 0 && (
          <ul className={styles.oneOffList}>
            {oneOff.map((b) => (
              <li key={b.break_id} className={styles.oneOffRow}>
                <span>
                  {formatDate(b.break_date)} · {hhmm(b.start_time)}–
                  {hhmm(b.end_time)}
                  {b.label ? ` · ${b.label}` : ''}
                </span>
                <button
                  type="button"
                  className={styles.removeX}
                  onClick={() => remove(b.break_id)}
                  aria-label="Remove this break"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {oneOff && oneOff.length === 0 && (
          <p className={styles.muted}>No one-off breaks coming up.</p>
        )}
      </Card>

      {pending && (
        <AffectedBookingsDialog
          range={pending}
          what="break"
          saving={addOneOff.isPending}
          onConfirm={submitOneOff}
          onClose={() => setPending(null)}
        />
      )}
    </>
  )
}
